import { useEffect, useMemo, type ReactNode } from 'react';

import { useRfdDragIslandProps } from '@src/providers/RfdDragIslandContext';
import { matchesPredictionOutcomeSearch } from '@src/types/PredictionMarket';
import {
  Box,
  Button,
  ButtonActions,
  ButtonVariants,
  Flex,
  FormControlSizes,
  FormGroup,
  HStack,
  IconButton,
  IconName,
  LoaderSizes,
  LoaderTalos,
  SearchSelect,
  Stack,
} from '@talos/kyoko';

import { PredictionCardMarketSelector } from './PredictionCardMarketSelector';
import {
  getEventDescription,
  getEventFavoriteSymbol,
  getEventLabel,
  getEventTabGroup,
  getMarketLabel,
  getOutcomeDescription,
  getOutcomeFavoriteSymbol,
  getOutcomeLabel,
  getSeriesDescription,
  getSeriesFavoriteSymbol,
  getSeriesLabel,
  getSeriesTabGroup,
  type MarketOption,
} from './predictionSelectorFields';
import { PREDICTION_CARD_SELECTOR_DROPDOWN_WIDTH } from './styles';
import {
  usePredictionCardForm,
  type PredictionCardFormMode,
  type PredictionCardFormValues,
} from './usePredictionCardForm';
import { usePredictionFavoriteSymbol } from './usePredictionFavoriteSymbol';

interface PredictionCardFormProps {
  mode: PredictionCardFormMode;
  initialValues: PredictionCardFormValues;
  onValuesChange?: (values: PredictionCardFormValues) => void;
  onClose?: () => void;
  children: (args: { form: PredictionCardFormValues; fields: ReactNode; primaryDisabled: boolean }) => ReactNode;
}

export function PredictionCardForm({
  mode,
  initialValues,
  onValuesChange,
  onClose,
  children,
}: PredictionCardFormProps) {
  const rfdDragIslandProps = useRfdDragIslandProps();
  const { createFavoriteRenderResult } = usePredictionFavoriteSymbol();
  const {
    form,
    showMarketField,
    marketOptions,
    seriesOptions,
    eventOptions,
    outcomeOptions,
    marketAccountItems,
    isRefDataReady,
    handleMarketChange,
    handleEventSeriesChange,
    handleEventChange,
    handleOutcomeChange,
    handleMarketAccountsChange,
  } = usePredictionCardForm(mode, initialValues);

  useEffect(() => {
    onValuesChange?.(form);
  }, [form, onValuesChange]);

  const testIdPrefix = mode === 'create' ? 'prediction-card' : 'prediction-card-settings';
  // Market Account list appears with Outcome once Event is selected and is required to create/save.
  const primaryDisabled = !isRefDataReady || !form.event || form.selectedMarketAccounts.length === 0;

  const seriesRenderResult = useMemo(
    () => createFavoriteRenderResult(getSeriesFavoriteSymbol),
    [createFavoriteRenderResult]
  );

  const eventRenderResult = useMemo(
    () => createFavoriteRenderResult(getEventFavoriteSymbol),
    [createFavoriteRenderResult]
  );

  const outcomeRenderResult = useMemo(
    () => createFavoriteRenderResult(getOutcomeFavoriteSymbol),
    [createFavoriteRenderResult]
  );

  // Category tabs only when Series is unset — series already scopes the event list.
  const showEventCategoryTabs = !form.eventSeries;

  const eventSelect = (
    <SearchSelect
      w="100%"
      id={`${testIdPrefix}-event`}
      selection={form.event}
      options={eventOptions}
      onChange={handleEventChange}
      getLabel={getEventLabel}
      getDescription={getEventDescription}
      getTabGroup={showEventCategoryTabs ? getEventTabGroup : undefined}
      showGroupTabs={showEventCategoryTabs}
      renderResult={eventRenderResult}
      dropdownWidth={PREDICTION_CARD_SELECTOR_DROPDOWN_WIDTH}
      dropdownPlacement="bottom-start"
      maxHeight={240}
      portalize
      data-testid={`${testIdPrefix}-event-select`}
    />
  );

  const showSettingsClose = mode === 'settings' && !!onClose;
  // Place Close on the topmost settings field (Market when shown, otherwise Series).
  const settingsCloseButton = showSettingsClose ? (
    <IconButton
      ghost
      icon={IconName.Close}
      onClick={onClose}
      size={FormControlSizes.Small}
      data-testid="prediction-card-settings-slide-close"
    />
  ) : null;

  const marketSelect = (
    <SearchSelect
      w="100%"
      id={`${testIdPrefix}-market`}
      selection={marketOptions.find(option => option.name === form.market)}
      options={marketOptions}
      onChange={(option: MarketOption | undefined) => handleMarketChange(option?.name)}
      getLabel={getMarketLabel}
      dropdownWidth={PREDICTION_CARD_SELECTOR_DROPDOWN_WIDTH}
      dropdownPlacement="bottom-start"
      maxHeight={240}
      portalize
      data-testid={`${testIdPrefix}-market-select`}
    />
  );

  const eventSeriesSelect = (
    <SearchSelect
      w="100%"
      id={`${testIdPrefix}-event-series`}
      selection={form.eventSeries}
      options={seriesOptions}
      onChange={handleEventSeriesChange}
      getLabel={getSeriesLabel}
      getDescription={getSeriesDescription}
      getTabGroup={getSeriesTabGroup}
      showGroupTabs
      renderResult={seriesRenderResult}
      dropdownWidth={PREDICTION_CARD_SELECTOR_DROPDOWN_WIDTH}
      dropdownPlacement="bottom-start"
      maxHeight={240}
      portalize
      showClear
      data-testid={`${testIdPrefix}-event-series-select`}
    />
  );

  const fieldGroupMb = 'spacing8' as const;

  const fields = !isRefDataReady ? (
    <Flex flex="1" w="100%" minHeight={0} flexDirection="column">
      {showSettingsClose ? (
        <HStack w="100%" justifyContent="flex-end" mb="spacing8">
          {settingsCloseButton}
        </HStack>
      ) : null}
      <Flex
        flex="1"
        w="100%"
        minHeight={0}
        alignItems="center"
        justifyContent="center"
        data-testid={`${testIdPrefix}-ref-data-loading`}
      >
        <LoaderTalos size={LoaderSizes.SMALL} delay={150} text="Loading data" />
      </Flex>
    </Flex>
  ) : (
    <Stack w="100%" {...(rfdDragIslandProps ?? {})}>
      {showMarketField ? (
        <FormGroup label="Market" controlId={`${testIdPrefix}-market`} w="100%" mb={fieldGroupMb}>
          {showSettingsClose ? (
            <HStack w="100%" gap="spacing4" alignItems="center">
              <Box flex="1" minWidth={0}>
                {marketSelect}
              </Box>
              {settingsCloseButton}
            </HStack>
          ) : (
            <Box w="100%" minWidth={0}>
              {marketSelect}
            </Box>
          )}
        </FormGroup>
      ) : null}
      <FormGroup label="Series (optional)" controlId={`${testIdPrefix}-event-series`} w="100%" mb={fieldGroupMb}>
        {!showMarketField && showSettingsClose ? (
          <HStack w="100%" gap="spacing4" alignItems="center">
            <Box flex="1" minWidth={0}>
              {eventSeriesSelect}
            </Box>
            {settingsCloseButton}
          </HStack>
        ) : (
          <Box w="100%" minWidth={0}>
            {eventSeriesSelect}
          </Box>
        )}
      </FormGroup>
      <FormGroup label="Event" controlId={`${testIdPrefix}-event`} w="100%" mb={fieldGroupMb}>
        <Box w="100%" minWidth={0}>
          {eventSelect}
        </Box>
      </FormGroup>
      <FormGroup label="Outcome (optional)" controlId={`${testIdPrefix}-outcome`} w="100%" mb={fieldGroupMb}>
        <Box w="100%" minWidth={0}>
          <SearchSelect
            w="100%"
            id={`${testIdPrefix}-outcome`}
            selection={form.outcome}
            options={outcomeOptions}
            onChange={handleOutcomeChange}
            getLabel={getOutcomeLabel}
            getDescription={getOutcomeDescription}
            additionalSearchMatcher={matchesPredictionOutcomeSearch}
            renderResult={outcomeRenderResult}
            dropdownWidth={PREDICTION_CARD_SELECTOR_DROPDOWN_WIDTH}
            dropdownPlacement="bottom-start"
            showDescriptionInButton
            maxHeight={240}
            portalize
            showClear
            disabled={outcomeOptions.length === 0}
            data-testid={`${testIdPrefix}-outcome-select`}
          />
        </Box>
      </FormGroup>
      {form.event ? (
        <FormGroup label="Market Account" controlId={`${testIdPrefix}-market-account`} w="100%" mb={fieldGroupMb}>
          <PredictionCardMarketSelector
            items={marketAccountItems}
            selectedMarketAccounts={form.selectedMarketAccounts}
            selectedAggregation={form.selectedAggregation}
            onChangeSelectedMarketAccounts={handleMarketAccountsChange}
            data-testid={`${testIdPrefix}-market-account-list`}
          />
        </FormGroup>
      ) : null}
    </Stack>
  );

  return (
    <>
      {children({
        form,
        fields,
        primaryDisabled,
      })}
    </>
  );
}

interface PredictionCardFormActionsProps {
  mode: PredictionCardFormMode;
  primaryDisabled: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onDelete?: () => void;
}

export function PredictionCardFormActions({
  mode,
  primaryDisabled,
  isSubmitting,
  onCancel,
  onDelete,
}: PredictionCardFormActionsProps) {
  if (mode === 'create') {
    return (
      <ButtonActions gap="spacing8">
        <Button
          size={FormControlSizes.Small}
          type="button"
          onClick={onCancel}
          variant={ButtonVariants.Priority}
          data-testid="prediction-card-cancel-button"
          flex="1"
        >
          Cancel
        </Button>
        <Button
          size={FormControlSizes.Small}
          variant={ButtonVariants.Positive}
          type="submit"
          disabled={primaryDisabled}
          data-testid="prediction-card-create-button"
          flex="1"
        >
          Create a Card
        </Button>
      </ButtonActions>
    );
  }

  return (
    <ButtonActions gap="spacing8">
      <Button
        size={FormControlSizes.Small}
        type="button"
        onClick={onDelete}
        variant={ButtonVariants.Negative}
        data-testid="prediction-card-settings-delete-button"
        flex="1"
      >
        Delete Card
      </Button>
      <Button
        size={FormControlSizes.Small}
        variant={ButtonVariants.Primary}
        type="submit"
        disabled={primaryDisabled || isSubmitting}
        data-testid="prediction-card-settings-update-button"
        flex="1"
      >
        {isSubmitting ? 'Saving...' : 'Update'}
      </Button>
    </ButtonActions>
  );
}
